/**
 * Callout — React (web) implementation.
 *
 * Styling references the Callout.ts constants and design-system tokens.
 * No raw hex values or pixel font sizes in this file.
 */

import React from 'react';
import { Icon } from '../Icon/Icon.web';
import { calloutAccessibleLabel } from './calloutAccessibleLabel';
import type { IconName } from '../../elements/icons';
import type { CalloutTone } from './Callout';
import {
  calloutBackgroundForTone,
  CALLOUT_RADIUS,
  CALLOUT_PADDING,
  CALLOUT_ICON_SIZE,
  CALLOUT_TEXT_COLOR,
} from './Callout';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import { states } from '../../elements/states';
import { typography } from '../../elements/typography';

// Web/Callout (web components sheet): a 16/22 bold title over a 14/22 body,
// 12px between icon and text, 4px between title and body, and the icon lined up
// with the top of the text. Native keeps the shared Callout sizes.
const WEB_CALLOUT_TITLE_TYPOGRAPHY = typography.b2Bold;
const WEB_CALLOUT_BODY_TYPOGRAPHY = typography.b3Regular;
const WEB_CALLOUT_GAP = spacing.sm;
const WEB_CALLOUT_TEXT_GAP = spacing.xs;

// Web draws the success tone like the other tones: a filled check-circle and a
// green border on the shared green fill.
const WEB_SUCCESS_LOOK = {
  background: calloutBackgroundForTone.success,
  border: colors.stroke.success,
  icon: 'tick-with-container',
  iconColor: colors.icon.primary,
} as const satisfies { background: string; border: string; icon: IconName; iconColor: string };

/**
 * Web-only tones. The served catalog schema still only accepts `success`, so
 * GenUI documents cannot author these; web screens pass them directly.
 */
export type WebCalloutTone = CalloutTone | 'info' | 'warning' | 'error';

type AddedTone = Exclude<WebCalloutTone, CalloutTone>;

const ADDED_TONE_LOOK: Record<
  AddedTone,
  { background: string; border: string; icon: IconName; iconColor: string }
> = {
  info: {
    background: colors.background.callout,
    border: colors.stroke.callout,
    icon: 'info',
    iconColor: colors.icon.primary,
  },
  warning: {
    background: colors.coral['100'],
    border: colors.yellow['400'],
    icon: 'warning',
    iconColor: colors.icon.primary,
  },
  error: {
    background: colors.background.container,
    border: colors.button.critical,
    icon: 'alert',
    iconColor: colors.text.alert,
  },
};

export interface WebCalloutProps {
  tone?: WebCalloutTone;
  title?: string;
  body: string;
  icon?: IconName;
  'data-testid'?: string;
  'aria-label'?: string;
  /**
   * Web only. A control drawn under the body, inside the callout, for the one
   * thing the member can do about it (for example "Try again" on an error).
   */
  action?: React.ReactNode;
}

export function Callout({
  tone = 'success',
  title,
  body,
  icon,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  action,
}: WebCalloutProps) {
  const look = tone === 'success' ? WEB_SUCCESS_LOOK : ADDED_TONE_LOOK[tone];
  const iconName = icon ?? look.icon;

  // Every tone shares one layout: a bordered card with the icon at the top.
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: WEB_CALLOUT_GAP,
    padding: CALLOUT_PADDING,
    borderRadius: CALLOUT_RADIUS,
    backgroundColor: look.background,
    border: `${states.borderWidth.default}px solid ${look.border}`,
  };

  // A long unbroken run (an email address in the title) wraps rather than
  // running past the card.
  const titleStyle: React.CSSProperties = {
    fontFamily: WEB_CALLOUT_TITLE_TYPOGRAPHY.fontFamily,
    fontWeight: WEB_CALLOUT_TITLE_TYPOGRAPHY.fontWeight,
    fontSize: WEB_CALLOUT_TITLE_TYPOGRAPHY.fontSize,
    lineHeight: `${WEB_CALLOUT_TITLE_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: WEB_CALLOUT_TITLE_TYPOGRAPHY.letterSpacing,
    color: CALLOUT_TEXT_COLOR,
    overflowWrap: 'anywhere',
  };

  const bodyStyle: React.CSSProperties = {
    fontFamily: WEB_CALLOUT_BODY_TYPOGRAPHY.fontFamily,
    fontWeight: WEB_CALLOUT_BODY_TYPOGRAPHY.fontWeight,
    fontSize: WEB_CALLOUT_BODY_TYPOGRAPHY.fontSize,
    lineHeight: `${WEB_CALLOUT_BODY_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: WEB_CALLOUT_BODY_TYPOGRAPHY.letterSpacing,
    color: CALLOUT_TEXT_COLOR,
    overflowWrap: 'anywhere',
  };

  return (
    <div
      style={containerStyle}
      role={tone === 'error' ? 'alert' : 'status'}
      data-testid={dataTestId}
      aria-label={ariaLabel ?? calloutAccessibleLabel(title, body)}
    >
      {iconName && (
        <Icon name={iconName} size={CALLOUT_ICON_SIZE} color={look.iconColor} aria-hidden />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          gap: WEB_CALLOUT_TEXT_GAP,
        }}
      >
        {title ? <span style={titleStyle}>{title}</span> : null}
        <span style={bodyStyle}>{body}</span>
        {action ? <div style={{ alignSelf: 'flex-start' }}>{action}</div> : null}
      </div>
    </div>
  );
}
